import { useCallback, useEffect, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useSupabaseClient } from '../hooks/useSupabaseClient';
import { fetchEmotes, reactionEmotes, type Emote } from '../utils/emotes';

interface ReactionRow {
  emote_id: string;
  user_id: string;
}

/**
 * Slack/Facebook tarzı tepki butonları. Her emote ayrı açılıp kapanabilir;
 * aynı kullanıcı aynı emote'a tekrar basınca tepkisi geri alınır. Sayaç ve
 * tekilleştirme sunucu tarafında (Supabase RLS + UNIQUE constraint) tutulur.
 */
export function ReactionBar({ contentType, contentId, className }: { contentType: string; contentId: string; className?: string }) {
  const { user } = useUser();
  const getSupabase = useSupabaseClient();
  const [reactionSet, setReactionSet] = useState<Emote[]>([]);
  const [rows, setRows] = useState<ReactionRow[]>([]);
  const [pendingEmoteId, setPendingEmoteId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [emotes, supabase] = await Promise.all([fetchEmotes(), getSupabase()]);
    setReactionSet(reactionEmotes(emotes));
    const { data, error } = await supabase
      .from('reactions')
      .select('emote_id, user_id')
      .eq('content_type', contentType)
      .eq('content_id', contentId);
    if (error) {
      console.error('Tepkiler alınamadı:', error);
      return;
    }
    setRows((data as ReactionRow[]) ?? []);
  }, [contentType, contentId, getSupabase]);

  useEffect(() => { load(); }, [load]);

  if (reactionSet.length === 0) return null;

  async function toggle(emoteId: string) {
    if (!user || pendingEmoteId) return;
    setPendingEmoteId(emoteId);
    const already = rows.some(r => r.emote_id === emoteId && r.user_id === user.id);

    // İyimser güncelleme
    setRows(prev => already ? prev.filter(r => !(r.emote_id === emoteId && r.user_id === user.id)) : [...prev, { emote_id: emoteId, user_id: user.id }]);

    try {
      const supabase = await getSupabase();
      if (already) {
        const { error } = await supabase
          .from('reactions')
          .delete()
          .eq('content_type', contentType)
          .eq('content_id', contentId)
          .eq('emote_id', emoteId)
          .eq('user_id', user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('reactions').insert({
          content_type: contentType,
          content_id: contentId,
          emote_id: emoteId,
          user_id: user.id,
        });
        if (error) throw error;
      }
    } catch (err) {
      console.error('Tepki güncellenemedi:', err);
      load(); // geri al
    } finally {
      setPendingEmoteId(null);
    }
  }

  return (
    <div className={className ?? 'flex items-center gap-1 mt-2 flex-wrap'}>
      {reactionSet.map(emote => {
        const count = rows.filter(r => r.emote_id === emote.id).length;
        const mine = user ? rows.some(r => r.emote_id === emote.id && r.user_id === user.id) : false;
        return (
          <button
            key={emote.id}
            type="button"
            onClick={() => toggle(emote.id)}
            disabled={!user || pendingEmoteId === emote.id}
            title={emote.name}
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold border transition-colors disabled:opacity-60 ${
              mine
                ? 'bg-accent-500/10 border-accent-500 text-accent-600'
                : 'bg-surface border-border text-text-muted hover:border-accent-400'
            }`}
          >
            <img src={emote.image_url!} alt={emote.name} className="h-4 w-4" loading="lazy" />
            {count > 0 && <span>{count}</span>}
          </button>
        );
      })}
    </div>
  );
}
